import { View } from "@novorender/api";
import { vec3 } from "gl-matrix";
import { forwardRef, useEffect, useImperativeHandle, useMemo, useRef } from "react";

import { useAppSelector } from "app/redux-store-interactions";
import { CanvasHudLineLabel } from "components/canvasHud/lineLabel";
import { ensureLabelIsOnScreen, LineLabel, updateLineLabel2d } from "components/canvasHud/lineLabelMath";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import {
    axisColors,
    type AxisPartName,
    isAxisPartName,
    measureLineLabelBgColor,
    measureLineLabelTextColor,
} from "features/engine2D";
import { useUom } from "features/uom/hooks/useUom";
import { UnitType } from "features/uom/spec";
import { convertAndFormat } from "features/uom/utils";

import { selectObjectMeasureResult } from "./measureSlice";
import { useObjectMeasureAxisDrawProduct } from "./objectMeasureDraw";

type ObjectMeasureLabel = LineLabel & { text: string };

/** Keeps an axis label from landing on top of the distance label when the two nearly coincide. */
const MIN_AXIS_LABEL_Y_DIST = 20;

const LINE_LABEL_ID = "om-line-label";
const axisLabelId = (axis: AxisPartName) => `om-${axis}-label`;

/**
 * The distance labels for the object-to-object measurement: the total on the connecting line, and
 * one per axis on its dashed line. Rendered as HTML rather than canvas text, in the same pill style
 * point-to-point uses, and positioned every frame through the shared `update` handle.
 */
export const ObjectMeasureHtmlInteractions = forwardRef(function ObjectMeasureHtmlInteractions(_props, ref) {
    const {
        state: { view, size },
    } = useExplorerGlobals();
    const result = useAppSelector(selectObjectMeasureResult);
    const uom = useUom();
    const axisDrawProduct = useObjectMeasureAxisDrawProduct(view, result);
    const containerRef = useRef<HTMLDivElement>(null);

    const { lineLabel, axisLabels } = useMemo(() => {
        const from = result?.measureInfoA?.point;
        const to = result?.measureInfoB?.point;
        if (!result || !from || !to || result.distance === undefined) {
            return { lineLabel: undefined, axisLabels: [] as [AxisPartName, ObjectMeasureLabel][] };
        }

        const format = (distance: number) => convertAndFormat(uom, distance, UnitType.length, { decimals: 3 });

        const distances: Record<AxisPartName, number> = {
            "x-axis": result.distanceX,
            "y-axis": result.distanceY,
            "z-axis": result.distanceZ,
        };

        const axes: [AxisPartName, ObjectMeasureLabel][] = [];
        for (const part of axisDrawProduct?.objects[0]?.parts ?? []) {
            const [start, end] = part.vertices3D;
            if (!isAxisPartName(part.name) || !start || !end) {
                continue;
            }
            axes.push([
                part.name,
                {
                    text: format(distances[part.name]),
                    position: vec3.lerp(vec3.create(), start, end, 0.5),
                    start,
                    end,
                    dist2d: 0,
                },
            ]);
        }

        return {
            lineLabel: {
                text: format(result.distance),
                position: vec3.lerp(vec3.create(), from, to, 0.5),
                start: from,
                end: to,
                dist2d: 0,
            } as ObjectMeasureLabel,
            axisLabels: axes,
        };
    }, [result, axisDrawProduct, uom]);

    const position = useMemo(
        () => () => {
            if (!view || !containerRef.current) {
                return;
            }
            updateLabels2d(view, size.width, size.height, lineLabel, axisLabels);
            updateElementPositions(containerRef.current, lineLabel, axisLabels);
        },
        [view, size, lineLabel, axisLabels],
    );

    useImperativeHandle(ref, () => ({ update: position }), [position]);

    // The labels are mounted with no position of their own, so place them once up front rather than
    // waiting for the next frame to move them in from off screen.
    useEffect(position, [position]);

    return (
        <div ref={containerRef}>
            {lineLabel && (
                <CanvasHudLineLabel
                    id={LINE_LABEL_ID}
                    bgColor={measureLineLabelBgColor}
                    textColor={measureLineLabelTextColor}
                >
                    {lineLabel.text}
                </CanvasHudLineLabel>
            )}
            {axisLabels.map(([axis, label]) => (
                <CanvasHudLineLabel
                    key={axis}
                    id={axisLabelId(axis)}
                    bgColor={axisColors[axis].labelBg}
                    textColor={axisColors[axis].labelText}
                >
                    {axis[0].toUpperCase()}: {label.text}
                </CanvasHudLineLabel>
            ))}
        </div>
    );
});

function updateLabels2d(
    view: View,
    width: number,
    height: number,
    lineLabel: ObjectMeasureLabel | undefined,
    axisLabels: [AxisPartName, ObjectMeasureLabel][],
) {
    const toScreenSpace = view.convert.worldSpaceToScreenSpaceConverter({ round: false, clipFarPlane: true });

    for (const label of [lineLabel, ...axisLabels.map(([, label]) => label)]) {
        if (!label) {
            continue;
        }
        updateLineLabel2d(label, view, toScreenSpace);
        ensureLabelIsOnScreen(label, width, height, 70);
    }

    // Axis lines meet the measured line at its ends, so their labels can end up stacked on the
    // distance label. Push them clear of it, the way point-to-point does.
    for (const [, label] of axisLabels) {
        if (
            !label.position2d ||
            !lineLabel?.position2d ||
            Math.abs(label.position2d[0] - lineLabel.position2d[0]) >= 50 ||
            Math.abs(label.position2d[1] - lineLabel.position2d[1]) >= MIN_AXIS_LABEL_Y_DIST
        ) {
            continue;
        }
        const gap = label.position2d[1] - lineLabel.position2d[1];
        label.position2d[1] += gap > 0 ? MIN_AXIS_LABEL_Y_DIST - gap : -(MIN_AXIS_LABEL_Y_DIST + gap);
    }
}

function updateElementPositions(
    container: HTMLDivElement,
    lineLabel: ObjectMeasureLabel | undefined,
    axisLabels: [AxisPartName, ObjectMeasureLabel][],
) {
    const place = (id: string, label: ObjectMeasureLabel | undefined) => {
        const element = container.querySelector(`#${id}`) as HTMLElement | null;
        if (!element) {
            return;
        }
        element.style.left = `${label?.position2d?.[0] ?? -100}px`;
        element.style.top = `${label?.position2d?.[1] ?? -100}px`;
    };

    place(LINE_LABEL_ID, lineLabel);
    for (const [axis, label] of axisLabels) {
        place(axisLabelId(axis), label);
    }
}
